import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Send, CheckCheck, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/status";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";

export default function WhatsAppPage() {
  const { can } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [stats, setStats] = useState({});
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState({ whatsapp_events: {} });
  const [metaTemplates, setMetaTemplates] = useState({});

  const load = () => {
    api.get("/whatsapp/messages").then((r) => setMsgs(r.data));
    api.get("/whatsapp/stats").then((r) => setStats(r.data));
    api.get("/whatsapp/templates").then((r) => setTemplates(r.data));
    api.get("/settings").then((r) => { setSettings(r.data); setMetaTemplates(r.data.meta_templates || {}); });
  };
  useEffect(() => { load(); }, []);

  const toggleEvent = async (code, val) => {
    const ev = { ...settings.whatsapp_events, [code]: val };
    setSettings({ ...settings, whatsapp_events: ev });
    await api.put("/settings/whatsapp", { whatsapp_events: ev });
    toast.success("Impostazioni salvate");
  };

  const saveMeta = async () => {
    await api.put("/settings/whatsapp", { meta_templates: metaTemplates });
    toast.success("Template Meta salvati");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">WhatsApp</h1>
        {settings.whatsapp_live ? (
          <p className="text-sm font-medium text-emerald-600" data-testid="wa-live-status">Invio REALE attivo (Meta Cloud API) — se un messaggio è FAILED verifica token e template su Meta</p>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="wa-live-status">Modalità simulata — configura le chiavi Meta per l'invio reale</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Card className="p-5"><div className="mb-2 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950/40"><Send className="h-4 w-4" /></div><div className="font-heading text-2xl font-extrabold">{stats.sent ?? 0}</div><div className="text-xs text-muted-foreground">Inviati</div></Card>
        <Card className="p-5"><div className="mb-2 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40"><CheckCheck className="h-4 w-4" /></div><div className="font-heading text-2xl font-extrabold">{stats.delivered ?? 0}</div><div className="text-xs text-muted-foreground">Consegnati</div></Card>
        <Card className="p-5"><div className="mb-2 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-rose-50 text-rose-600 dark:bg-rose-950/40"><XCircle className="h-4 w-4" /></div><div className="font-heading text-2xl font-extrabold">{stats.failed ?? 0}</div><div className="text-xs text-muted-foreground">Falliti</div></Card>
      </div>

      <Tabs defaultValue="messages">
        <TabsList>
          <TabsTrigger value="messages" data-testid="wa-tab-messages">Messaggi</TabsTrigger>
          <TabsTrigger value="templates" data-testid="wa-tab-templates">Template</TabsTrigger>
          {can("whatsapp.config") && <TabsTrigger value="config" data-testid="wa-tab-config">Configurazione</TabsTrigger>}
        </TabsList>

        <TabsContent value="messages" className="space-y-2 pt-4">
          {msgs.map((m) => (
            <Card key={m.id} className="p-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> {m.template_name} · {m.phone}</span>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">{m.status}</span>
              </div>
              <p className="mt-2 text-sm">{m.body}</p>
              <div className="mt-1 text-xs text-muted-foreground">{fmtDate(m.sent_at)}</div>
            </Card>
          ))}
          {msgs.length === 0 && <Card className="p-12 text-center text-muted-foreground">Nessun messaggio inviato.</Card>}
        </TabsContent>

        <TabsContent value="templates" className="space-y-3 pt-4">
          {templates.map((t) => (
            <Card key={t.code} className="p-5">
              <div className="font-semibold">{t.name}</div>
              <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{t.body}</p>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="config" className="space-y-4 pt-4">
          <Card className="divide-y divide-border">
            {templates.map((t) => (
              <div key={t.code} className="flex items-center justify-between px-5 py-3">
                <span className="text-sm font-medium">{t.name}</span>
                <Switch data-testid={`wa-toggle-${t.code}`} checked={settings.whatsapp_events?.[t.code] !== false}
                  onCheckedChange={(v) => toggleEvent(t.code, v)} />
              </div>
            ))}
          </Card>
          <Card className="space-y-3 p-5">
            <div className="font-semibold">Template Meta approvati</div>
            <p className="text-xs text-muted-foreground">Inserisci il nome esatto del template approvato su Meta per ogni evento. Se compilato, l'invio userà il template (valido anche oltre le 24h); altrimenti verrà inviato testo libero (solo entro 24h dall'ultimo messaggio del cliente).</p>
            {templates.map((t) => (
              <div key={t.code} className="flex flex-wrap items-center gap-3">
                <span className="w-48 text-sm">{t.name}</span>
                <Input className="flex-1" data-testid={`wa-meta-${t.code}`} placeholder="es. repair_ready_it"
                  value={metaTemplates[t.code] || ""}
                  onChange={(e) => setMetaTemplates({ ...metaTemplates, [t.code]: e.target.value })} />
              </div>
            ))}
            <Button data-testid="save-meta-btn" onClick={saveMeta}>Salva template</Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
